import { invoke } from '@tauri-apps/api/core'

import { apiService, IS_TAURI } from '@/services/apiService'
import { resolveApiBaseUrl } from '@/services/apiUrlService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { showSuccess } from '@/services/toastService'
import type {
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  EditProfileRequest,
  EditProfileResponse,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  PasswordResetRequest,
  PasswordResetResponse,
  PasswordResetConfirmRequest,
  ResetPasswordResponse,
  SignupRequest,
  SignupResponse,
} from '@/types/user'

const HTTP_URL_PATTERN = /^https?:\/\//i
const TAURI_DEV_ORIGIN_PATTERN = /^https?:\/\/tauri\.localhost(?::\d+)?(?:[/?#]|$)/i
const LOCAL_NATIVE_DEV_API_BASE_URL = 'http://127.0.0.1:8000/api/v1'
const ANDROID_EMULATOR_NATIVE_DEV_API_BASE_URL = 'http://10.0.2.2:8000/api/v1'

function isAndroidUserAgent(userAgent: string): boolean {
  return /\bAndroid\b/i.test(userAgent)
}

function isTauriDevApiUrl(url: string): boolean {
  if (!HTTP_URL_PATTERN.test(url)) {
    return true
  }

  return TAURI_DEV_ORIGIN_PATTERN.test(url)
}

export function resolveNativeApiBaseUrl({
  explicitNativeOrigin = import.meta.env.VITE_NATIVE_API_ORIGIN?.trim(),
  resolvedApiBaseUrl = resolveApiBaseUrl(),
  isDev = import.meta.env.DEV,
  userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '',
}: {
  explicitNativeOrigin?: string
  resolvedApiBaseUrl?: string | null
  isDev?: boolean
  userAgent?: string
} = {}): string {
  if (explicitNativeOrigin) {
    return explicitNativeOrigin
  }

  if (resolvedApiBaseUrl && !isTauriDevApiUrl(resolvedApiBaseUrl)) {
    return resolvedApiBaseUrl
  }

  if (isDev) {
    return isAndroidUserAgent(userAgent)
      ? ANDROID_EMULATOR_NATIVE_DEV_API_BASE_URL
      : LOCAL_NATIVE_DEV_API_BASE_URL
  }

  return resolvedApiBaseUrl || '/api/v1'
}

class AuthService {
  private buildApiEndpointUrl(endpoint: string): string {
    const baseUrl = resolveNativeApiBaseUrl()
    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
    return `${normalizedBaseUrl}${normalizedEndpoint}`
  }

  private async loginWithTauriTokenEndpoint(request: LoginRequest): Promise<LoginResponse> {
    const url = this.buildApiEndpointUrl('/accounts/token-login/')

    try {
      return await invoke<LoginResponse>('native_token_login', {
        input: {
          url,
          email: request.email,
          password: request.password,
        },
      })
    } catch (error) {
      logService.error(LOG_CATEGORIES.AUTH, 'Native token login failed', { url, error })
      throw error
    }
  }

  /**
   * Sign up a new user
   */
  async signup(request: SignupRequest): Promise<SignupResponse> {
    const result = await apiService.post<SignupResponse>('/accounts/signup/', request, undefined, {
      suppressToast: true,
    })

    if (result.success) {
      showSuccess(result.message)
    }

    return result
  }

  /**
   * Log in a user
   */
  async login(request: LoginRequest): Promise<LoginResponse> {
    const result = IS_TAURI
      ? await this.loginWithTauriTokenEndpoint(request)
      : await apiService.post<LoginResponse>('/accounts/cookie-login/', request, undefined, {
          suppressToast: true,
        })

    return result
  }

  /**
   * Log out the current user
   */
  async logout(): Promise<LogoutResponse> {
    const result = await apiService.post<LogoutResponse>('/accounts/logout/')

    if (result.success) {
      showSuccess(result.message)
    }

    return result
  }

  /**
   * Request a password reset
   */
  async requestPasswordReset(request: PasswordResetRequest): Promise<PasswordResetResponse> {
    const result = await apiService.post<PasswordResetResponse>(
      '/accounts/request-password-reset/',
      request,
    )

    if (result.success) {
      showSuccess(result.message)
    }

    return result
  }

  /**
   * Confirm password reset with key
   */
  async confirmPasswordReset(request: PasswordResetConfirmRequest): Promise<ResetPasswordResponse> {
    const result = await apiService.post<ResetPasswordResponse>(
      '/accounts/reset-password/',
      request,
    )

    if (result.success) {
      showSuccess(result.message)
    }

    return result
  }

  /**
   * Edit user profile
   */
  async editProfile(request: EditProfileRequest): Promise<EditProfileResponse> {
    const result = await apiService.post<EditProfileResponse>('/accounts/edit-profile/', request)

    if (result.success) {
      showSuccess(result.message)
    }

    return result
  }

  /**
   * Accept project invitation
   */
  async acceptInvitation(
    token: string,
    request?: AcceptInvitationRequest,
  ): Promise<AcceptInvitationResponse> {
    const result = await apiService.acceptInvitation(token, request)

    if (result.success) {
      showSuccess(result.message)
    }

    // Convert the API response to match our expected format
    return {
      success: result.success,
      message: result.message,
      workspaceClientId: result.workspaceClientId || '',
      userId: result.userId || 0,
      userEmail: result.userEmail || '',
      userProfileName: result.userProfileName || '',
    }
  }
}

// Export as singleton
export const authService = new AuthService()
